<template>
    <b-container fluid class="p-4">
        <!-- 목록 카드 -->
        <b-card>
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <span class="font-weight-bold">스니펫 관리</span>
                    <b-button variant="success" size="sm" @click="openAddModal">
                        스니펫 추가
                    </b-button>
                </div>
            </template>

            <!-- 검색 툴바 -->
            <b-row class="mb-3">
                <b-col cols="12" md="6" class="mb-2 mb-md-0">
                    <b-form-input
                        v-model="keyword"
                        placeholder="코드 또는 라벨로 검색"
                        size="sm"
                        :disabled="listLoading"
                        @keyup.enter="searchData"
                    />
                </b-col>
                <b-col cols="12" md="6" class="text-md-right">
                    <b-button variant="primary" size="sm" :disabled="listLoading" class="mr-1" @click="searchData">검색</b-button>
                    <b-button variant="outline-secondary" size="sm" :disabled="listLoading" @click="resetSearch">초기화</b-button>
                </b-col>
            </b-row>

            <b-table
                :items="snippets"
                :fields="columns"
                :busy="listLoading"
                show-empty
                responsive
                striped
                hover
            >
                <template #cell(index)="row">
                    {{ (pagination.currentPage - 1) * pagination.perPage + row.index + 1 }}
                </template>

                <template #cell(detail)="row">
                    <b-button variant="outline-secondary" size="sm" @click="openDetailModal(row.item)">
                        상세
                    </b-button>
                </template>

                <template #cell(updated_at)="row">
                    {{ getDate(row.value) }}
                </template>

                <template #empty>
                    <PageEmptyComponent text="조회된 스니펫이 없습니다." />
                </template>

                <template #table-busy>
                    <div class="text-center my-3">
                        <b-spinner variant="primary" />
                    </div>
                </template>
            </b-table>

            <div class="d-flex justify-content-center mt-3">
                <b-pagination
                    v-model="pagination.currentPage"
                    :total-rows="pagination.totalRows"
                    :per-page="pagination.perPage"
                    @change="onPageChange"
                />
            </div>
        </b-card>

        <!-- 스니펫 모달 -->
        <b-modal
            id="snippet-modal"
            :title="modalTitle"
            hide-footer
            no-close-on-backdrop
            size="xl"
            @hidden="resetSnippetForm"
        >
            <!-- add 모드 -->
            <template v-if="modalMode === 'add'">
                <b-form>
                    <b-form-group label="코드" label-for="snippet-code">
                        <b-form-input
                            id="snippet-code"
                            v-model="form.code"
                            placeholder="스니펫 코드 입력"
                        />
                    </b-form-group>

                    <b-form-group label="버전" label-for="snippet-version">
                        <b-form-input
                            id="snippet-version"
                            v-model.number="form.version"
                            type="number"
                            min="1"
                            placeholder="버전 입력"
                        />
                    </b-form-group>

                    <b-form-group label="라벨" label-for="snippet-label">
                        <b-form-input
                            id="snippet-label"
                            v-model="form.label"
                            placeholder="라벨 입력"
                        />
                    </b-form-group>

                    <b-form-group label="설명" label-for="snippet-description">
                        <b-form-input
                            id="snippet-description"
                            v-model="form.description"
                            placeholder="설명 입력"
                        />
                    </b-form-group>

                    <b-form-group label="내용">
                        <SnippetEditor v-model="form.content" />
                    </b-form-group>
                </b-form>

                <div class="d-flex justify-content-end mt-3">
                    <b-button
                        variant="primary"
                        :disabled="saving"
                        class="mr-2"
                        @click="saveSnippet"
                    >
                        추가
                    </b-button>
                    <b-button variant="secondary" :disabled="saving" @click="$bvModal.hide('snippet-modal')">
                        취소
                    </b-button>
                </div>
            </template>

            <!-- detail / edit 모드 -->
            <template v-else>
                <!-- 상단 메타 정보 -->
                <b-row class="mb-3">
                    <b-col cols="4">
                        <b-form-group label="코드">
                            <b-form-input :value="selectedSnippet && selectedSnippet.code" readonly />
                        </b-form-group>
                    </b-col>
                    <b-col cols="2">
                        <b-form-group label="버전">
                            <b-form-input :value="selectedSnippet && selectedSnippet.version" readonly />
                        </b-form-group>
                    </b-col>
                    <b-col cols="6">
                        <b-form-group label="라벨">
                            <b-form-input
                                v-model="form.label"
                                :readonly="modalMode === 'detail'"
                                placeholder="라벨"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mb-3">
                    <b-col cols="12">
                        <b-form-group label="설명">
                            <b-form-input
                                v-model="form.description"
                                :readonly="modalMode === 'detail'"
                                placeholder="설명"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>

                <!-- 에디터 + 버전 타임라인 -->
                <b-row>
                    <b-col cols="8">
                        <SnippetEditor
                            v-model="form.content"
                            :readonly="modalMode === 'detail'"
                        />
                    </b-col>
                    <b-col cols="4">
                        <SnippetVersionTimeline
                            v-if="selectedSnippet"
                            :snippet-code="selectedSnippet.code"
                            :current-version="selectedSnippet.version"
                            @version-selected="onVersionSelected"
                        />
                    </b-col>
                </b-row>

                <!-- 액션 버튼 -->
                <div class="d-flex justify-content-between mt-3">
                    <div>
                        <b-button
                            v-if="modalMode !== 'add'"
                            variant="outline-danger"
                            :disabled="saving"
                            @click="deleteSnippet"
                        >
                            삭제
                        </b-button>
                    </div>
                    <div>
                        <template v-if="modalMode === 'detail'">
                            <b-button variant="primary" @click="modalMode = 'edit'">
                                수정
                            </b-button>
                        </template>
                        <template v-else>
                            <b-button
                                variant="primary"
                                :disabled="saving"
                                class="mr-2"
                                @click="saveSnippet"
                            >
                                저장
                            </b-button>
                            <b-button variant="secondary" :disabled="saving" @click="$bvModal.hide('snippet-modal')">
                                취소
                            </b-button>
                        </template>
                    </div>
                </div>
            </template>
        </b-modal>
    </b-container>
</template>

<script>
import PaginationPageMixin from '@shared_mixins/pagination-page-mixins.js';
import { columns } from './data.js';
import { getDefaultPagination, getDate } from '../../common-data.js';
import SnippetEditor from './_SubComponent/SnippetEditor.vue';
import SnippetVersionTimeline from './_SubComponent/SnippetVersionTimeline.vue';
import PageEmptyComponent from '@shared_components/admin/components/common/PageEmptyComponent.vue';

export default {
    name: 'MailSnippet',

    components: {
        SnippetEditor,
        SnippetVersionTimeline,
        PageEmptyComponent,
    },

    mixins: [
        PaginationPageMixin({
            pagination: getDefaultPagination(1, 20),
        }),
    ],

    data() {
        return {
            keyword: '',
            snippets: [],
            pagination: getDefaultPagination(1, 20),
            modalMode: 'add',
            selectedSnippet: null,
            form: {
                code: '',
                version: 1,
                label: '',
                description: '',
                content: '',
            },
            saving: false,
            columns,
        };
    },

    computed: {
        modalTitle() {
            if (this.modalMode === 'add') return '스니펫 추가';
            if (this.modalMode === 'edit') return '스니펫 수정';
            return '스니펫 상세';
        },
    },

    mounted() {
        this.fetchData();
    },

    methods: {
        getDate,

        async fetchData() {
            this.listLoading = true;
            try {
                const { data } = await this.$axios.get('/mail/snippet', {
                    params: {
                        page: this.pagination.currentPage,
                        size: this.pagination.perPage,
                        keyword: this.keyword || undefined,
                    },
                });
                this.snippets = data.results || data.items || data;
                this.pagination.totalRows = data.total ?? (this.snippets.length);
            } catch (e) {
                this.$bvToast.toast('데이터를 불러오는데 실패했습니다.', {
                    variant: 'danger',
                    title: '오류',
                });
            } finally {
                this.listLoading = false;
            }
        },

        onPageChange(page) {
            this.pagination.currentPage = page;
            this.fetchData();
        },

        searchData() {
            this.pagination.currentPage = 1;
            this.fetchData();
        },

        resetSearch() {
            this.keyword = '';
            this.pagination.currentPage = 1;
            this.fetchData();
        },

        openAddModal() {
            this.resetSnippetForm();
            this.modalMode = 'add';
            this.$bvModal.show('snippet-modal');
        },

        async openDetailModal(item) {
            this.selectedSnippet = { ...item };
            this.form.code = item.code;
            this.form.version = item.version;
            this.form.label = item.label;
            this.form.description = item.description || '';
            this.form.content = item.content || '';
            this.modalMode = 'detail';
            this.$bvModal.show('snippet-modal');
            // 목록 API는 content를 포함하지 않으므로 상세 API로 fetch
            if (!item.content) {
                try {
                    const { data } = await this.$axios.get(`/mail/snippet/${item.code}/${item.version}`);
                    this.form.content = data.content || '';
                    this.selectedSnippet = { ...this.selectedSnippet, content: data.content };
                } catch (e) { /* silent */ }
            }
        },

        async onVersionSelected(v) {
            this.selectedSnippet = { ...v };
            this.form.label = v.label;
            // content가 없으면 API로 상세 조회
            if (v.content !== undefined && v.content !== null) {
                this.form.content = v.content;
            } else {
                try {
                    const { data } = await this.$axios.get(`/mail/snippet/${v.code}/${v.version}`);
                    this.form.content = data.content || '';
                    this.selectedSnippet = { ...this.selectedSnippet, content: this.form.content };
                } catch (e) {
                    this.$bvToast.toast('버전 내용을 불러오는데 실패했습니다.', {
                        variant: 'danger',
                        title: '오류',
                    });
                }
            }
        },

        async saveSnippet() {
            this.saving = true;
            try {
                if (this.modalMode === 'add') {
                    await this.$axios.post('/mail/snippet', {
                        code: this.form.code,
                        version: this.form.version,
                        label: this.form.label,
                        description: this.form.description,
                        content: this.form.content,
                    });
                } else {
                    await this.$axios.patch(
                        `/mail/snippet/${this.selectedSnippet.code}/${this.selectedSnippet.version}`,
                        {
                            label: this.form.label,
                            description: this.form.description,
                            content: this.form.content,
                        }
                    );
                }
                this.$bvToast.toast('저장되었습니다.', {
                    variant: 'success',
                    title: '성공',
                });
                await this.fetchData();
                this.$bvModal.hide('snippet-modal');
            } catch (e) {
                if (e.response && e.response.status === 409) {
                    this.$bvToast.toast('이미 존재하는 코드/버전입니다.', {
                        variant: 'danger',
                        title: '오류',
                    });
                } else {
                    this.$bvToast.toast('저장에 실패했습니다.', {
                        variant: 'danger',
                        title: '오류',
                    });
                }
            } finally {
                this.saving = false;
            }
        },

        async deleteSnippet() {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `v${this.selectedSnippet.version} 버전을 삭제하시겠습니까?`,
                { title: '삭제 확인', okVariant: 'danger', okTitle: '삭제', cancelTitle: '취소' }
            );
            if (!confirmed) return;

            this.saving = true;
            try {
                await this.$axios.delete(
                    `/mail/snippet/${this.selectedSnippet.code}/${this.selectedSnippet.version}`
                );
                this.$bvToast.toast('삭제되었습니다.', {
                    variant: 'success',
                    title: '성공',
                });
                this.$bvModal.hide('snippet-modal');
                await this.fetchData();
            } catch (e) {
                const status = e.response?.status;
                if (status === 409 || status === 400) {
                    this.$bvToast.toast('사용 중인 스니펫은 삭제할 수 없습니다.', {
                        variant: 'danger',
                        title: '오류',
                    });
                } else {
                    this.$bvToast.toast('삭제에 실패했습니다.', {
                        variant: 'danger',
                        title: '오류',
                    });
                }
            } finally {
                this.saving = false;
            }
        },

        resetSnippetForm() {
            this.selectedSnippet = null;
            this.form = {
                code: '',
                version: 1,
                label: '',
                description: '',
                content: '',
            };
        },
    },
};
</script>
